import moment from 'moment'

/**
 * 格式化日期
 * @param {Date|String|Number} date - 日期对象、日期字符串或时间戳
 * @param {String} format - 格式化模式，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns {String} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return ''
  return moment(date).format(format)
}

/**
 * 初始化微信 JS-SDK 配置
 * @param {Array} jsApiList - 需要使用的 JS 接口列表
 * @returns {Promise} Promise 对象
 */
export function initWxConfig(jsApiList = []) {
  return new Promise((resolve, reject) => {
    if (!window.wx) {
      reject(new Error('微信 JS-SDK 未加载'))
      return
    }

    // 获取微信配置
    import('@/api/config').then(module => {
      const api = module.default
      api.get('/wechat/js-config', {
        params: {
          url: encodeURIComponent(window.location.href.split('#')[0])
        }
      })
      .then(response => {
        const config = response.data
        
        window.wx.config({
          debug: process.env.NODE_ENV === 'development',
          appId: config.appId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList: jsApiList
        })
        
        window.wx.ready(() => {
          resolve()
        })
        
        window.wx.error(error => {
          reject(error)
        })
      })
      .catch(error => {
        reject(error)
      })
    })
  })
}

/**
 * 防抖函数
 * @param {Function} func - 需要防抖的函数
 * @param {Number} wait - 等待时间，单位毫秒
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, wait = 300) {
  let timeout
  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      func.apply(this, args)
    }, wait)
  }
}

/**
 * 节流函数
 * @param {Function} func - 需要节流的函数
 * @param {Number} limit - 时间限制，单位毫秒
 * @returns {Function} 节流后的函数
 */
export function throttle(func, limit = 300) {
  let inThrottle
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * 获取设备类型
 * @returns {String} 设备类型：mobile, tablet, desktop
 */
export function getDeviceType() {
  const ua = navigator.userAgent
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet'
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile'
  }
  return 'desktop'
}

// 本地存储封装
export const storage = {
  set(key, value) {
    if (typeof value === 'object') {
      value = JSON.stringify(value)
    }
    localStorage.setItem(key, value)
  },
  get(key) {
    const value = localStorage.getItem(key)
    try {
      return JSON.parse(value)
    } catch (e) {
      return value
    }
  },
  remove(key) {
    localStorage.removeItem(key)
  }
}

/**
 * 主题管理工具
 */
export const ThemeManager = {
  /**
   * 主题变更事件回调函数
   */
  listeners: [],
  
  /**
   * 添加主题变更监听器
   * @param {Function} callback 回调函数
   */
  addThemeChangeListener(callback) {
    if (typeof callback === 'function' && !this.listeners.includes(callback)) {
      this.listeners.push(callback);
    }
  },
  
  /**
   * 移除主题变更监听器
   * @param {Function} callback 回调函数
   */
  removeThemeChangeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  },
  
  /**
   * 触发主题变更事件
   * @param {String} theme 主题名称
   */
  notifyThemeChange(theme) {
    this.listeners.forEach(callback => {
      try {
        callback(theme);
      } catch (e) {
        console.error('主题变更回调执行错误:', e);
      }
    });
  },
  
  /**
   * 获取当前主题
   * @returns {String} 当前主题名称
   */
  getCurrentTheme() {
    return localStorage.getItem('theme') || 'neutral';
  },
  
  /**
   * 应用主题
   * @param {String} theme 主题名称
   */
  applyTheme(theme) {
    const root = document.documentElement;
    
    switch (theme) {
      case 'warm':
        root.style.setProperty('--primary-color', '#F97316');
        root.style.setProperty('--secondary-color', '#FDBA74');
        root.style.setProperty('--accent-color', '#EA580C');
        root.style.setProperty('--background-color', '#FFF7ED');
        root.style.setProperty('--primary-light', '#FB923C');
        root.style.setProperty('--primary-dark', '#C2410C');
        root.style.setProperty('--gradient-header', 'linear-gradient(to right, #FFF7ED, #FFEDD5)');
        
        // 设置RGB值以支持透明度
        root.style.setProperty('--primary-color-rgb', '249, 115, 22');
        root.style.setProperty('--accent-color-rgb', '234, 88, 12');
        break;
        
      case 'monochrome':
        root.style.setProperty('--primary-color', '#0ABAB5');
        root.style.setProperty('--secondary-color', '#81D8D0');
        root.style.setProperty('--accent-color', '#00827F');
        root.style.setProperty('--background-color', '#E0F5F4');
        root.style.setProperty('--primary-light', '#4BC7C3');
        root.style.setProperty('--primary-dark', '#006D6A');
        root.style.setProperty('--gradient-header', 'linear-gradient(to right, #E0F5F4, #C5EBE9)');
        
        // 设置RGB值以支持透明度
        root.style.setProperty('--primary-color-rgb', '10, 186, 181');
        root.style.setProperty('--accent-color-rgb', '0, 130, 127');
        break;
        
      default: // neutral
        root.style.setProperty('--primary-color', '#3B82F6');
        root.style.setProperty('--secondary-color', '#93C5FD');
        root.style.setProperty('--accent-color', '#2563EB');
        root.style.setProperty('--background-color', '#F3F4F6');
        root.style.setProperty('--primary-light', '#60A5FA');
        root.style.setProperty('--primary-dark', '#1D4ED8');
        root.style.setProperty('--gradient-header', 'linear-gradient(to right, #EBF5FF, #DBEAFE)');
        
        // 设置RGB值以支持透明度
        root.style.setProperty('--primary-color-rgb', '59, 130, 246');
        root.style.setProperty('--accent-color-rgb', '37, 99, 235');
        break;
    }
    
    // 保存主题设置到本地存储
    localStorage.setItem('theme', theme);
    
    // 触发主题变更事件
    this.notifyThemeChange(theme);
  }
}; 